import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Crown, LogOut, ShieldX } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MENUS, ROLE_CLS, ROLE_LABEL } from "@/lib/staff";
import { IMG } from "@/lib/assets";
import { ReportsPanel } from "@/pages/admin/ReportsPanel";
import { OrdersPanel } from "@/pages/admin/OrdersPanel";
import { BuybacksPanel } from "@/pages/admin/BuybacksPanel";
import { InjectPanel } from "@/pages/admin/InjectPanel";
import { HistoryPanel } from "@/pages/admin/HistoryPanel";
import { AppearancePanel } from "@/pages/admin/AppearancePanel";
import { MaintenancePanel } from "@/pages/admin/MaintenancePanel";
import { StaffPanel } from "@/pages/admin/StaffPanel";
import { GroupsPanel } from "@/pages/admin/GroupsPanel";
import { ActivityPanel } from "@/pages/admin/ActivityPanel";
import { AccountPanel } from "@/pages/admin/AccountPanel";
import { SettingsPanel } from "@/pages/AdminSettings";
import { GamesPopupPanel } from "@/pages/admin/GamesPopupPanel";
import { useLiveFeed, SoundToggle } from "@/components/admin/LiveFeed";

const PANELS = {
  ringkasan: ReportsPanel,
  transaksi: OrdersPanel,
  bongkar: BuybacksPanel,
  inject: InjectPanel,
  riwayat: HistoryPanel,
  tampilan: AppearancePanel,
  pengaturan: SettingsPanel,
  "game-popup": GamesPopupPanel,
  maintenance: MaintenancePanel,
  akun: StaffPanel,
  bagian: GroupsPanel,
  aktivitas: ActivityPanel,
  "akun-saya": AccountPanel,
};

const AccessDenied = () => (
  <div className="rk-glass max-w-lg rounded-2xl p-8 text-center" data-testid="access-denied">
    <ShieldX className="mx-auto h-10 w-10 text-rose-400" />
    <h3 className="mt-3 text-lg font-bold">Akses Ditolak</h3>
    <p className="mt-1 text-sm text-zinc-400">Menu ini tidak diaktifkan untuk akun Anda. Hubungi MASTER jika perlu akses.</p>
  </div>
);

const Sidebar = ({ menus, active, counts, user, logout }) => (
  <aside className="border-b border-white/5 bg-[#0c0c12] lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col lg:border-b-0 lg:border-r" data-testid="admin-aside">
    <div className="flex items-center gap-3 border-b border-white/5 px-5 py-4">
      <img src={IMG.logo} alt="JP STORE" className="h-9 w-auto" />
      <div className="leading-tight">
        <div className="text-sm font-extrabold tracking-wide text-[#e5b80b]">JP STORE</div>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Admin Panel</div>
      </div>
    </div>
    <nav className="flex gap-1 overflow-x-auto px-3 py-3 lg:flex-1 lg:flex-col lg:overflow-y-auto" data-testid="admin-sidebar">
      {menus.map(({ key, label, icon: Icon }) => (
        <Link
          key={key}
          to={`/admin/${key}`}
          className={`relative inline-flex shrink-0 items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-colors ${
            active === key ? "border border-[#e5b80b]/30 bg-[#e5b80b]/10 font-semibold text-[#e5b80b]" : "border border-transparent text-zinc-400 hover:bg-white/5 hover:text-white"
          }`}
          data-testid={`menu-${key}`}
          ref={active === key ? (el) => el?.scrollIntoView({ inline: "center", block: "nearest" }) : undefined}
        >
          <Icon size={16} /> {label}
          {counts[key] ? (
            <span className="ml-auto rounded-full bg-rose-500 px-1.5 text-[10px] font-bold leading-4 text-white" data-testid={`menu-count-${key}`}>{counts[key]}</span>
          ) : null}
        </Link>
      ))}
    </nav>
    <div className="hidden space-y-2 border-t border-white/5 p-3 lg:block">
      <div className="flex items-center gap-2 rounded-xl border border-[#e5b80b]/30 bg-[#e5b80b]/10 px-3 py-2 text-xs font-bold text-[#e5b80b]">
        <Crown size={13} /> <span className="truncate">{user.username}</span>
        <span className={`ml-auto rounded px-1.5 py-0.5 text-[9px] ${ROLE_CLS[user.role]}`}>{ROLE_LABEL[user.role]}</span>
      </div>
      <button onClick={logout} className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm text-rose-400 transition-colors hover:bg-rose-500/10" data-testid="admin-logout">
        <LogOut size={15} /> Keluar
      </button>
    </div>
  </aside>
);

export const AdminShell = () => {
  const { user, logout, can, refresh } = useAuth();
  const { menu } = useParams();
  const feed = useLiveFeed(logout);
  const counts = { transaksi: feed.topup?.unseen || 0, bongkar: feed.bongkar?.unseen || 0 };

  useEffect(() => {
    refresh();
  }, [menu, refresh]);

  const menus = MENUS.filter((m) => m.allowed(user, can));
  if (!menu) return <Navigate to={`/admin/${menus[0].key}`} replace />;
  const current = MENUS.find((m) => m.key === menu);
  const allowed = current && current.allowed(user, can);
  const Panel = allowed ? PANELS[menu] : AccessDenied;

  return (
    <div className="relative z-10 min-h-screen bg-[#08080c]" data-testid="admin-dashboard">
      <Sidebar menus={menus} active={menu} counts={counts} user={user} logout={logout} />
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-white/5 bg-[#0c0c12]/90 px-4 py-3 backdrop-blur-xl lg:px-8">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Panel JP STORE</div>
            <h1 className="truncate text-base font-bold">{current?.label || "Dashboard"}</h1>
          </div>
          <div className="flex items-center gap-2">
            {can("orders") || can("buybacks") ? <SoundToggle /> : null}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold">{user.name}</div>
              <div className="font-mono text-[10px] text-zinc-500">@{user.username} · <span data-testid="current-role">{ROLE_LABEL[user.role]}</span></div>
            </div>
            <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#e5b80b] to-[#a8860a] text-sm font-black uppercase text-black">{(user.name || user.username || "?").slice(0, 1)}</span>
            <button onClick={logout} className="rk-btn-ghost inline-flex items-center gap-1 text-xs lg:hidden" data-testid="admin-logout-mobile">
              <LogOut size={13} /> Keluar
            </button>
          </div>
        </header>
        <main className="mx-auto max-w-[1600px] px-4 pb-24 pt-6 lg:px-8">
          <Panel />
        </main>
      </div>
    </div>
  );
};
